// @flow

// Be robust to various HOC wrappers (connect, withRouter, etc.)
// Some libs hoist statics onto the wrapper, others keep them on the inner component.
const getDataDependency = (component: any = {}, methodName: string) => {
  if (!component) return null;

  // 1) Try on the wrapper itself first (if statics were hoisted)
  if (typeof component[methodName] === 'function') {
    return component[methodName];
  }

  // 2) Otherwise unwrap common inner-component keys and try again
  const inner =
    component.WrappedComponent ||
    component.DecoratedComponent ||
    component.Component ||
    component.Inner ||
    component.InnerComponent ||
    null;

  return inner ? getDataDependency(inner, methodName) : null;
};

function fetchDataDependencies(
  routes: Array<{ route: Object, match: Object }>,
  args: Object,
  deferred: boolean
) {
  const methodName = deferred ? 'fetchDataDeferred' : 'fetchData';

  return routes.reduce((promises, entry) => {
    const route = entry && entry.route;
    const match = entry && entry.match;
    if (!route || !match) return promises;

    const component = route.component;
    if (component) {
      const fetchData = getDataDependency(component, methodName);
      if (fetchData) {
        // Merge any existing params with the matched params for this route
        const params = Object.assign(
          {},
          args.params || {},
          (match && match.params) || {}
        );

        // IMPORTANT: pass both merged params AND the full match object
        const p = fetchData(
          Object.assign({}, args, {
            params,
            match
          })
        );

        // Ensure we always push a thenable to keep Promise.all stable
        promises.push(Promise.resolve(p));
      }
    }

    return promises;
  }, []);
}

type Options = {
  location: Object,
  store: {
    getState: () => any,
    dispatch: (action: any) => any
  },
  routes: Array<{
    route: {
      component: Object,
      routes?: Array<Object>
    },
    match: {
      path: string,
      url: string,
      params: Object,
      isExact: boolean
    }
  }>,
  renderProps?: {
    components: Array<Object>,
    params: Object
  },
  deferred?: boolean,
  reload?: boolean
};

export function handleDataDependencies({
  location,
  store,
  routes,
  deferred = false,
  reload = false
}: Options) {
  const { getState, dispatch } = store;

  return Promise.all(
    fetchDataDependencies(
      routes,
      { store, location, getState, dispatch, reload },
      deferred
    )
  );
}
